import React, { useState, useCallback } from "react";
import axios from "axios";

const API = "/api/zerodha";
const INDICES = ["NIFTY", "BANKNIFTY", "SENSEX"];

function fmt(n) {
  if (n === null || n === undefined) return "—";
  if (typeof n !== "number") return String(n);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function pos(spot, level) {
  if (spot == null || level == null) return null;
  return spot > level ? "above" : spot < level ? "below" : "at";
}

function Section({ data, error, loading }) {
  if (loading) return <div style={{ padding: 12, color: "var(--dim2)" }}>Loading…</div>;
  if (error) return <div style={{ padding: 12, color: "#ef4444" }}>{error}</div>;
  if (!data) return <div style={{ padding: 12, color: "var(--dim2)" }}>No data yet. Click Refresh.</div>;

  const { spot, oi, hilo, pivots, cpr, vwap, emas, asOfIST } = data;

  return (
    <div style={{ padding: 12 }}>
      <div style={{ fontSize: 12, color: "var(--dim2)", marginBottom: 6 }}>
        As of {asOfIST} &middot; Spot: <strong style={{ color: "var(--text2)" }}>{fmt(spot)}</strong>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
        {/* OI walls + max pain */}
        <div style={{ border: "1px solid var(--border2)", borderRadius: 6, padding: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#f59e0b", marginBottom: 8 }}>
            OI Walls &middot; Max Pain
          </div>
          {!oi && <div style={{ fontSize: 12, color: "var(--dim2)" }}>OI data unavailable (auth required)</div>}
          {oi && (
            <>
              <div style={{ fontSize: 12, color: "var(--dim3)", marginBottom: 6 }}>
                ATM: <strong>{fmt(oi.atm)}</strong> &middot; Expiry: <strong>{oi.expiry}</strong> &middot;
                Max Pain: <strong style={{ color: "#a78bfa" }}>{fmt(oi.maxPain)}</strong> &middot;
                PCR: <strong>{fmt(oi.pcr)}</strong>
              </div>
              <div style={{ fontSize: 11, color: "var(--dim2)", marginBottom: 4 }}>Bias: <strong style={{ color: "var(--text2)" }}>{oi.bias}</strong></div>
              <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ color: "var(--dim2)", textAlign: "left" }}>
                    <th>Resistance (CE walls)</th>
                    <th>OI (Lakh)</th>
                    <th>Support (PE walls)</th>
                    <th>OI (Lakh)</th>
                  </tr>
                </thead>
                <tbody>
                  {[0, 1, 2, 3, 4].map(i => {
                    const ce = oi.ceWalls?.[i];
                    const pe = oi.peWalls?.[i];
                    return (
                      <tr key={i} style={{ borderTop: "1px solid var(--panel3)" }}>
                        <td style={{ color: "var(--pale-neg)" }}>{ce ? fmt(ce.strike) : "—"}</td>
                        <td>{ce ? (ce.oi / 1e5).toFixed(1) : "—"}</td>
                        <td style={{ color: "var(--pale-pos)" }}>{pe ? fmt(pe.strike) : "—"}</td>
                        <td>{pe ? (pe.oi / 1e5).toFixed(1) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>

        {/* H/L */}
        <div style={{ border: "1px solid var(--border2)", borderRadius: 6, padding: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#22d3ee", marginBottom: 8 }}>
            Prev-Day &middot; Today H/L
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 12 }}>
            <div>PDH: <strong style={{ color: "var(--pale-neg)" }}>{fmt(hilo?.pdh)}</strong></div>
            <div>HoD: <strong style={{ color: "var(--pale-neg)" }}>{fmt(hilo?.hod)}</strong></div>
            <div>PDC: <strong>{fmt(hilo?.pdc)}</strong></div>
            <div>VWAP: <strong style={{ color: "#a78bfa" }}>{fmt(vwap)}</strong></div>
            <div>PDL: <strong style={{ color: "var(--pale-pos)" }}>{fmt(hilo?.pdl)}</strong></div>
            <div>LoD: <strong style={{ color: "var(--pale-pos)" }}>{fmt(hilo?.lod)}</strong></div>
          </div>
        </div>

        {/* Pivots + CPR */}
        <div style={{ border: "1px solid var(--border2)", borderRadius: 6, padding: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#fbbf24", marginBottom: 8 }}>
            Floor Pivots &middot; CPR
          </div>
          {!pivots && <div style={{ fontSize: 12, color: "var(--dim2)" }}>Pivot data unavailable</div>}
          {pivots && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 12 }}>
              <div style={{ color: "var(--pale-neg)" }}>R3: <strong>{fmt(pivots.R3)}</strong></div>
              <div style={{ color: "var(--pale-neg)" }}>R2: <strong>{fmt(pivots.R2)}</strong></div>
              <div style={{ color: "var(--pale-neg)" }}>R1: <strong>{fmt(pivots.R1)}</strong></div>
              <div style={{ color: "var(--text2)" }}>P: <strong>{fmt(pivots.P)}</strong></div>
              <div style={{ color: "var(--pale-pos)" }}>S1: <strong>{fmt(pivots.S1)}</strong></div>
              <div style={{ color: "var(--pale-pos)" }}>S2: <strong>{fmt(pivots.S2)}</strong></div>
              <div style={{ color: "var(--pale-pos)" }}>S3: <strong>{fmt(pivots.S3)}</strong></div>
              {cpr && (
                <>
                  <div style={{ color: "var(--dim3)", gridColumn: "span 2", borderTop: "1px solid var(--panel3)", paddingTop: 4, marginTop: 4 }}>
                    CPR — TC: <strong>{fmt(cpr.tc)}</strong> &middot; Pivot: <strong>{fmt(cpr.pivot)}</strong> &middot; BC: <strong>{fmt(cpr.bc)}</strong>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* EMAs */}
        <div style={{ border: "1px solid var(--border2)", borderRadius: 6, padding: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#38bdf8", marginBottom: 8 }}>
            EMAs (15-min)
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, fontSize: 12 }}>
            <div>EMA20: <strong>{fmt(emas?.ema20)}</strong><div style={{ fontSize: 10, color: "var(--dim2)" }}>spot {pos(spot, emas?.ema20) || "—"}</div></div>
            <div>EMA50: <strong>{fmt(emas?.ema50)}</strong><div style={{ fontSize: 10, color: "var(--dim2)" }}>spot {pos(spot, emas?.ema50) || "—"}</div></div>
            <div>EMA200: <strong>{fmt(emas?.ema200)}</strong><div style={{ fontSize: 10, color: "var(--dim2)" }}>spot {pos(spot, emas?.ema200) || "—"}</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SRLevels() {
  const [byIndex, setByIndex] = useState({});
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  const fetchOne = useCallback(async (index) => {
    setLoading(prev => ({ ...prev, [index]: true }));
    setErrors(prev => ({ ...prev, [index]: null }));
    try {
      const res = await axios.get(`${API}/sr-levels?index=${index}`, { timeout: 30000 });
      if (res.data?.success) {
        setByIndex(prev => ({ ...prev, [index]: res.data.data }));
      } else {
        setErrors(prev => ({ ...prev, [index]: res.data?.message || "Failed" }));
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setErrors(prev => ({ ...prev, [index]: msg }));
    } finally {
      setLoading(prev => ({ ...prev, [index]: false }));
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    await Promise.all(INDICES.map(fetchOne));
    setRefreshing(false);
  }, [fetchOne]);

  return (
    <div style={{ padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <h2 style={{ margin: 0, color: "var(--text2)", fontSize: 18 }}>Intraday S&amp;R Levels</h2>
        <button
          onClick={refreshAll}
          disabled={refreshing}
          style={{ padding: "6px 14px", background: "#0ea5e9", color: "#fff", border: "none", borderRadius: 4, cursor: refreshing ? "wait" : "pointer", fontSize: 13 }}
        >
          {refreshing ? "Refreshing…" : "Refresh All"}
        </button>
        <span style={{ fontSize: 11, color: "var(--dim2)" }}>
          Manual refresh only. Needs Zerodha login for OI walls.
        </span>
      </div>

      {INDICES.map(idx => (
        <div key={idx} style={{ marginBottom: 18, border: "1px solid var(--panel3)", borderRadius: 8, background: "var(--panel2)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderBottom: "1px solid var(--panel3)" }}>
            <div style={{ color: "#f1f5f9", fontWeight: 600 }}>{idx}</div>
            <button
              onClick={() => fetchOne(idx)}
              disabled={loading[idx]}
              style={{ padding: "4px 10px", background: "var(--panel3)", color: "var(--dim3)", border: "1px solid var(--border2)", borderRadius: 4, fontSize: 12, cursor: loading[idx] ? "wait" : "pointer" }}
            >
              {loading[idx] ? "…" : "Refresh"}
            </button>
          </div>
          <Section data={byIndex[idx]} error={errors[idx]} loading={loading[idx]} />
        </div>
      ))}
    </div>
  );
}
